import 'dayjs/locale/fr';

import { IonButton, IonIcon } from '@ionic/react';
import { PfInput, PfSelect } from '@lidea/shared/ui/form';
import { FiltersOptions, ProtocolFormValues } from '@lidea/shared/util/types';
import { DatePickerInput } from '@mantine/dates';
import { isNotEmpty, useForm } from '@mantine/form';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { chevronDown } from 'ionicons/icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import footerStyles from '../form-panel-footer/form-panel-footer.module.css';
import stackStyles from '../form-panel-footer/form-panel-stack.module.css';
import styles from './protocol-form.module.css';

const protocolFields = ['contract_area'];

/* eslint-disable-next-line */
export interface ProtocolFormProps {
  initialValues?: ProtocolFormValues;
  geosectorField?: FiltersOptions['geosector'];
  contractCode?: string;
  formLayout?: 'fill' | 'scrollParent';
  /** Mobile modals: Ionic buttons (lidea-btn styles are desktop-only). */
  useIonicActions?: boolean;
  onSubmit: (event: ProtocolFormValues) => void;
  onBack?: () => void;
  onAdd?: (event: ProtocolFormValues) => void;
  onCancel?: () => void;
  i18n: {
    confirm: string;
    cancel: string;
    back?: string;
    add?: string;
  };
}

export function ProtocolForm(props: ProtocolFormProps) {
  const { t } = useTranslation(['common', 'plot'], { useSuspense: false });
  const initialValues = useMemo(() => {
    if (props.initialValues) {
      return {
        ...props.initialValues,
        contract_code:
          props.contractCode || props.initialValues.contract_code || undefined,
        sowing_date: props.initialValues.sowing_date
          ? dayjs(props.initialValues.sowing_date).toDate()
          : undefined,
      };
    }
    return { contract_code: props.contractCode };
  }, [props.initialValues, props.contractCode]);

  const form = useForm<ProtocolFormValues>({
    initialValues,
    validate: props.geosectorField
      ? {
          geosector: isNotEmpty(t('plot:geosector_required') as string),
        }
      : undefined,
  });

  const handleSubmit = (values: ProtocolFormValues, isAdd: boolean) => {
    const formattedValues = {
      ...values,
      sowing_date: values.sowing_date
        ? dayjs(values.sowing_date).format('YYYY-MM-DD')
        : undefined,
    };
    if (isAdd && props.onAdd) {
      // Add null check for props.onAdd
      props.onAdd(formattedValues);
    } else {
      props.onSubmit(formattedValues);
    }
  };

  const isScrollParent = props.formLayout === 'scrollParent';

  const fields = (
    <>
      {props.geosectorField ? (
        <PfSelect
          error={form.errors.geosector as string}
          label={t('plot:geosector') as string}
          options={props.geosectorField.options}
          pickerMode={props.useIonicActions ? 'modal' : 'dropdown'}
          placeholder={t('plot:select_placeholder') as string}
          value={
            form.values.geosector != null && form.values.geosector !== ''
              ? String(form.values.geosector)
              : undefined
          }
          onBlur={() => form.validateField('geosector')}
          onChange={(next) => {
            const parsed =
              next === '' || next == null ? undefined : Number(next);
            form.setFieldValue('geosector', parsed);
          }}
        />
      ) : null}

      <DatePickerInput
        classNames={{ root: styles.dateField }}
        label={t('plot:sowing_date') as string}
        locale="fr"
        placeholder={t(`plot:select_date_placeholder`) as string}
        rightSection={<IonIcon icon={chevronDown} />}
        valueFormat="D MMMM, YYYY"
        {...form.getInputProps('sowing_date')}
      />

      <PfInput
        label={t(`plot:contract_code`) as string}
        placeholder={t(`plot:input_placeholder`) as string}
        {...form.getInputProps('contract_code')}
      />

      <PfInput
        label={t(`plot:NFML`) as string}
        placeholder={t(`plot:input_placeholder`) as string}
        {...form.getInputProps('NFML')}
      />

      <PfSelect
        label={t('plot:irrigation') as string}
        options={[
          { value: true, label: t('common:yes') },
          { value: false, label: t('common:no') },
        ]}
        placeholder={t(`plot:select_placeholder`) as string}
        {...form.getInputProps('irrigation')}
      />

      {protocolFields.map((field) => (
        <PfInput
          key={field}
          label={t(`plot:${field}`) as string}
          placeholder={t(`plot:input_placeholder`) as string}
          type="number"
          {...form.getInputProps(field)}
        />
      ))}
    </>
  );

  const actions = props.useIonicActions ? (
    <>
      <input style={{ display: 'none' }} type="submit" />
      <IonButton
        color="danger"
        expand="block"
        type="button"
        onClick={props.onCancel}
      >
        {props.i18n.cancel}
      </IonButton>
      <IonButton color="success" expand="block" type="submit">
        {props.i18n.confirm}
      </IonButton>
    </>
  ) : (
    <>
      <input style={{ display: 'none' }} type="submit" />
      <button
        className="lidea-btn lidea-btn--danger"
        type="button"
        onClick={props.onCancel}
      >
        {props.i18n.cancel}
      </button>
      {props.onBack ? (
        <button
          className="lidea-btn lidea-btn--ghost"
          type="button"
          onClick={props.onBack}
        >
          {props.i18n.back}
        </button>
      ) : null}
      <button className="lidea-btn lidea-btn--filled" type="submit">
        {props.i18n.confirm}
      </button>
    </>
  );

  const actionsClassName = classNames(styles.actions, {
    [styles.actionsIonic]: props.useIonicActions,
  });

  return (
    <form
      className={classNames(styles.container, {
        [styles.containerScrollParent]: isScrollParent,
      })}
      onSubmit={form.onSubmit((values) => {
        handleSubmit(values, false);
      })}
    >
      {isScrollParent ? (
        <div className={stackStyles.stack}>
          <div
            className={classNames(
              styles['fields-container'],
              stackStyles.stackFields
            )}
          >
            {fields}
          </div>
          <div className={stackStyles.stackFooter}>
            {props.i18n.add ? (
              <div className={footerStyles.footerAddAction}>
                <button
                  className="lidea-btn lidea-btn--ghost lidea-btn--block"
                  type="button"
                  onClick={() => handleSubmit(form.values, true)}
                >
                  {props.i18n.add}
                </button>
              </div>
            ) : null}
            <div
              className={classNames(actionsClassName, footerStyles.footerActions)}
            >
              {actions}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={styles['fields-container']}>{fields}</div>
          {props.i18n.add ? (
            <div className={styles.addAction}>
              <button
                className="lidea-btn lidea-btn--ghost lidea-btn--block"
                type="button"
                onClick={() => handleSubmit(form.values, true)}
              >
                {props.i18n.add}
              </button>
            </div>
          ) : null}
          <div className={actionsClassName}>{actions}</div>
        </>
      )}
    </form>
  );
}

export default ProtocolForm;
